import { DataGrid, EmptyState, Icon, theme, Spinner, Chip, TableCell, Avatar, AvatarSize } from '@ketch-com/deck'
import { useMemo, useRef, useState } from 'react'
import { useDataSentryContext } from '../context/DataSentryContext'
import { useDataSentryTrackersList } from 'api/dataSentry/queries/useDataSentryTrackersList'
import { DataSentryTableFilters } from '../components/DataSentryTableFilters'
import { useTableFilters } from '../hooks/useTableFilters'
import { AuthorizationStatus as TrackerAuthorizationStatus } from '@ketch-com/windlass/dist/dataSentry/v1/trackers/trackers'
import { avatarIconByOrigin, originCopy } from 'pages/dataSentry/trackers/constants'
import { TrackerListItem, TrackerListItemOrigin } from '@ketch-com/windlass/dist/dataSentry/v1/trackers/trackers'
import { Box, Typography, Tooltip } from '@mui/material'
import { GridColDef, GridColumnHeaderParams, GridRenderCellParams } from '@mui/x-data-grid-premium'
import pluralize from 'pluralize'

export const TrackersTable: React.FC = () => {
  const [trackerAuthorizationStatus, setTrackerAuthorizationStatus] = useState<TrackerAuthorizationStatus>(
    TrackerAuthorizationStatus.AUTHORIZED,
  )

  const {
    searchQuery,
    debouncedSearchQuery,
    setSearchQuery,
    dataSystemFilterValue,
    paginationModel,
    setPaginationModel,
  } = useTableFilters()

  const { selectedTimeRange, selectedPropertyCode, setTrackerOverview, hasPropertyAndScanTimeRange } =
    useDataSentryContext()

  const { data: trackersList, isLoading } = useDataSentryTrackersList({
    params: {
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedPropertyCode,
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
      query: debouncedSearchQuery,
      'data-system-code': dataSystemFilterValue?.code,
      'authorization-status': trackerAuthorizationStatus,
    },
    enabled: hasPropertyAndScanTimeRange,
  })

  const rowCountRef = useRef(trackersList?.total || 0)
  const rowCount = useMemo(() => {
    if (trackersList?.total !== undefined) {
      rowCountRef.current = trackersList?.total
    }
    return rowCountRef.current
  }, [trackersList?.total])

  const columns: GridColDef<TrackerListItem>[] = [
    {
      align: 'left',
      field: 'name',
      headerName: 'Tracker',
      flex: 3,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<TrackerListItem>) => {
        return (
          <TableCell>
            <Box display="flex" alignItems="center" gap={1} maxWidth="100%">
              <Avatar
                icon={avatarIconByOrigin[row?.origin || TrackerListItemOrigin.TRACKER_ORIGIN_UNSPECIFIED]}
                textColor={theme.palette.Text.Secondary}
                variant="square"
                backgroundColor={theme.palette.Black.o8}
                size={AvatarSize.medium}
                sx={{ borderRadius: 1.5 }}
              />
              <Typography
                component="div"
                variant="label"
                sx={{
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  maxWidth: '100%',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setTrackerOverview?.(row)
                }}
              >
                {row.name}
              </Typography>
            </Box>
          </TableCell>
        )
      },
    },
    {
      align: 'left',
      field: 'origin',
      headerName: 'Origin',
      flex: 2,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<TrackerListItem>) => {
        return (
          <TableCell>
            <Typography>{originCopy[row?.origin || TrackerListItemOrigin.TRACKER_ORIGIN_UNSPECIFIED]}</Typography>
          </TableCell>
        )
      },
    },
    {
      align: 'left',
      field: 'purposes',
      headerName: 'Purposes',
      flex: 2,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<TrackerListItem>) => {
        return (
          <TableCell>
            {row?.purposes?.length ? (
              <Tooltip
                placement="bottom"
                title={
                  <Box display="flex" flexDirection="column" gap={0.5} maxHeight={120} overflow="auto">
                    {row?.purposes?.map(purpose => (
                      <Typography key={purpose?.code}>{purpose?.name || purpose?.code}</Typography>
                    ))}
                  </Box>
                }
              >
                <Box>
                  <Chip
                    size="small"
                    label={`${row.purposes?.length ? row.purposes.length : 0} ${pluralize(
                      'purpose',
                      row.purposes?.length === 1 ? 1 : 2,
                    )}`}
                  />
                </Box>
              </Tooltip>
            ) : (
              <Typography>Unknown</Typography>
            )}
          </TableCell>
        )
      },
    },
  ]
  const isLoadingTrackersList = isLoading
  const displayDataGridEmptyState = !isLoadingTrackersList && !trackersList?.trackers && !debouncedSearchQuery
  const displayDataGrid =
    !isLoadingTrackersList && (trackersList?.trackers || (!trackersList?.trackers && debouncedSearchQuery))

  if (isLoadingTrackersList)
    return (
      <Box display="flex" height={380} alignItems="center" justifyContent="center">
        <Spinner size="32px" thickness={2} />
      </Box>
    )

  return (
    <Box display="flex" flexDirection="column" gap={1} height={380}>
      <DataSentryTableFilters
        searchQuery={searchQuery}
        handleSearchInputChange={setSearchQuery}
        setTrackerAuthorizationStatus={setTrackerAuthorizationStatus}
        trackerAuthorizationStatus={trackerAuthorizationStatus}
        setPaginationModel={setPaginationModel}
      />

      {displayDataGridEmptyState ? (
        <EmptyState
          size="small"
          colorTheme={theme.palette.Text.Secondary}
          sx={{ backgroundColor: theme.palette.Black.o8, borderRadius: 4 }}
          customIcon={<Icon name="OTableView" width="48px" height="48px" iconColor={theme.palette.Black.o48} />}
          title="No Data"
          subTitle="Review your settings and connections, or wait for the scan to finish processing."
        />
      ) : null}

      {displayDataGrid ? (
        <DataGrid
          autosizeOnMount
          autosizeOptions={{
            expand: true,
            includeHeaders: true,
            includeOutliers: false,
          }}
          getRowHeight={() => 'auto'}
          columns={columns}
          getRowId={row => row.name}
          rows={trackersList?.trackers || []}
          checkboxSelection={false}
          hideFooter={false}
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnPinning
          disableColumnReorder
          disableColumnResize
          disableChildrenSorting={false}
          disableRowHoverStates
          disableBorder
          loading={isLoadingTrackersList}
          pagination
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 20]}
          noRowsOverlayProps={{ buttonTitle: '' }}
        />
      ) : null}
    </Box>
  )
}
